import {
    closestCenter,
    DndContext,
    KeyboardSensor,
    MouseSensor,
    TouchSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
    arrayMove,
    horizontalListSortingStrategy,
    SortableContext,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@jobber/components/Button';
import { SafeCombobox as Combobox } from '../../components/Common/SafeCombobox';
import { Glimmer } from '@jobber/components/Glimmer';
import { InputText } from '@jobber/components/InputText';
import { Option, Select } from '@jobber/components/Select';
import { Spinner } from '@jobber/components/Spinner';
import { showToast } from '@jobber/components/Toast';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table';
import React, { useMemo, useState } from 'react';
import { Dropdown, DropdownItem } from 'react-bootstrap';
import { IoMdArrowDropdown, IoMdArrowDropup } from 'react-icons/io';
import { useDispatch } from 'react-redux';
import Pagination from '../../components/Common/Pagination';
import { getFromServer, postToServer } from '../../components/Common/requests';
import { setFilterQuery } from '../../store/filterSlice';
import { useDebounce } from '../../utils/useDebounce';
import { useFilter } from './FilterQuery/FilterQuery';
import useTableColumns from './useTableColumns';
import InstructionToolTip from '../../components/PageInformation/instruction';

// Draggable header cell
const DraggableTableHeader = ({ header, onSort, getIconStyle }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: header.id });

    const { isDisabled, header: headerVal, filterKey } = header.column.columnDef

    return (
        <th
            ref={!!isDisabled ? undefined : setNodeRef}
            style={{
                transform: CSS.Translate?.toString(transform),
                transition,
                position: "relative",
                width: `calc(var(--header-${header?.id}-size) * 1px)`,
                zIndex: isDragging ? 1 : 0,
            }}
            className="border px-2 py-3"
        >
            {/* Column Order Handle */}
            <div {...attributes} {...listeners} className={`headDND ${!!isDisabled && 'disabledDND'}`} />
            <div className='d-flex justify-content-between align-items-center'>
                <b style={{ zIndex: 2 }} className='position-relative'>
                    {header.isPlaceholder
                        ? null
                        : flexRender(
                            headerVal,
                            header.getContext()
                        )}
                </b>
                {!!filterKey &&
                    <div
                        className='icon-container' onClick={(e) => {
                            e.stopPropagation();
                            onSort(filterKey);
                        }}>
                        <IoMdArrowDropup className={`icon-up ${getIconStyle(filterKey, 'ASCENDING')}`} />
                        <IoMdArrowDropdown className={`icon-down ${getIconStyle(filterKey, 'DESCENDING')}`} />
                    </div>
                }
            </div>

            {/* Resize Handle */}
            <div
                onMouseDown={header.getResizeHandler()}
                className={`${!!isDisabled ? 'd-none' : 'resizer'} ${header.column.getIsResizing() ? 'isResizing' : ''}`}
            />
        </th >
    );
};

const DragAlongCell = ({ cell, row }) => {
    const { isDragging, setNodeRef, transform, transition } = useSortable({
        id: cell.column.id,
    });

    const style = {
        position: 'relative',
        transform: CSS.Translate.toString(transform),
        transition,
        zIndex: isDragging ? 1 : 0,
    };

    return (
        <td style={style} ref={setNodeRef} className="p-2 position-relative">
            <a href={row.original.jobberWebUri} target="_blank" rel="noreferrer" className="style__none" style={{ position: 'absolute', inset: 0 }} />
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </td>
    );
};

export default function ClientTable({ toggleFilter }) {
    const dispatch = useDispatch();

    // Pagination and table state
    const [limit, setLimit] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    const { updateFilter, buildQuery, savedFilterId, addFilterList } = useFilter();

    const [selected, setSelected] = useState(() => {
        const savedFilter = localStorage.getItem('savedFilter');
        return savedFilter ? [{ id: savedFilter, label: savedFilter }] : [];
    });

    // Column Arrangement & Resizing State
    const localSettings = JSON.parse(localStorage.getItem('userSettings'));
    const [visibleColumns, setVisibleColumns] = useState(localSettings?.columnVisibility || {});
    const [columnOrder, setColumnOrder] = useState(localSettings?.columnOrder || []);
    const [columnSizing, setColumnSizing] = useState(localSettings?.columnWidths || {});

    // Mutation for saving user settings
    const { mutate: mutateSettings, isLoading: isSavePending } = useMutation({
        mutationFn: (body) => postToServer('table-settings', body),
        onError: (error) => {
            if (error && error.status === 404) {
                showToast({ message: 'Error 404: Table settings endpoint not found.', variation: 'error' });
            } else {
                showToast({ message: error.message || error.error || 'An error occurred', variation: 'error' });
            }
        },
        onSuccess: (data) => {
            localStorage.setItem('userSettings', JSON.stringify(data.data));
            showToast({ message: 'User settings updated successfully' });
        },
    });

    // Save settings handler using mutation
    const saveUserSettingsHandler = () => {
        const body = {
            columnOrder: columnOrder,
            columnVisibility: visibleColumns,
            columnWidths: columnSizing,
        };
        mutateSettings(body);
    };

    const queryString = buildQuery();

    // TanStack Query
    const { data: clientResponse, isLoading, refetch } = useQuery({
        queryKey: ['clients', queryString, savedFilterId, currentPage, limit],
        queryFn: ({ signal }) =>
            getFromServer(`clients?${queryString}${savedFilterId ? `&filterId=${savedFilterId}` : ''}`, signal),
        staleTime: 1000 * 60 * 5,
        retry: 2,
    });

    // Importing Table Columns
    const columns = useTableColumns();

    // React Table TanStack
    const table = useReactTable({
        data: clientResponse?.data || [],
        columns,
        defaultColumn: {
            minSize: 150,
            maxSize: 800,
        },
        state: { columnVisibility: visibleColumns, columnOrder, columnSizing },
        onColumnVisibilityChange: setVisibleColumns,
        onColumnOrderChange: setColumnOrder,
        onColumnSizingChange: setColumnSizing,
        columnResizeMode: 'onChange',
        manualSorting: true,
        getCoreRowModel: getCoreRowModel(),
    });

    // columnSizeVars method to compute and memoize widths
    const columnSizeVars = useMemo(() => {
        const headers = table.getFlatHeaders();
        const colSizes = {};
        for (let i = 0; i < headers.length; i++) {
            const header = headers[i];
            colSizes[`--header-${header.id}-size`] = header.getSize();
            colSizes[`--col-${header.column.id}-size`] = header.column.getSize();
        }
        localStorage.setItem('userSettings', JSON.stringify({ ...localSettings, columnWidths: columnSizing }));
        return colSizes;
    }, [table.getState().columnSizingInfo, table.getState().columnSizing]);

    // DnD sensors for dragging columns
    const sensors = useSensors(
        useSensor(MouseSensor, {}),
        useSensor(TouchSensor, {}),
        useSensor(KeyboardSensor, {})
    );

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (active && over && active.id !== over.id) {
            const oldIndex = columnOrder.indexOf(active.id);
            const newIndex = columnOrder.indexOf(over.id);
            const newOrder = arrayMove(columnOrder, oldIndex, newIndex);
            localStorage.setItem('userSettings', JSON.stringify({ ...localSettings, columnOrder: newOrder }));
            setColumnOrder(newOrder);
        }
    };

    const [showDropdown, setShowDropdown] = useState(false);
    const toggleDropdown = () => setShowDropdown((prev) => !prev);

    const [search, setSearch] = useState('');
    const debouncedUpdateFilter = useDebounce(updateFilter, 500);

    function formatLabel(key) {
        return key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase());
    }

    // Clear filters function
    const clearFilters = () => {
        dispatch(setFilterQuery({}));
        localStorage.removeItem('tagCloudInclude');
        localStorage.removeItem('tagCloudExclude');
        addFilterList({});
        updateFilter('limit', 10);
        setSearch('');
        setCurrentPage(1);
        refetch();
    };

    const [sortConfig, setSortConfig] = useState({
        key: null,
        direction: 'ASCENDING'
    });

    const onSort = (key) => {
        updateFilter('sort_key', key);
        if (sortConfig?.key === key && sortConfig?.direction === 'ASCENDING') {
            setSortConfig({ key, direction: 'DESCENDING' });
            updateFilter('sort_direction', 'DESCENDING');
        } else {
            setSortConfig({ key, direction: 'ASCENDING' });
            updateFilter('sort_direction', 'ASCENDING');
        }
    };

    const getIconStyle = (key, direction) => {
        return sortConfig.key === key && sortConfig.direction === direction ? 'fs-5 text-dark' : 'fs-5 text-muted opacity-25';
    };

    return (
        <div>
            <div className="search__sticky">
                <div className="d-flex justify-content-between mb-3">
                    <div className="col-lg-3 col-md-4 d-flex gap-2 align-items-center">
                        <p className="pt-3">Show</p>
                        <Select
                            size="small"
                            onChange={(e) => {
                                updateFilter('limit', e);
                                setLimit(e);
                            }}
                            value={limit}
                        >
                            <Option value="10">10</Option>
                            <Option value="20">20</Option>
                            <Option value="50">50</Option>
                        </Select>
                        <p className="pt-3">entries</p>
                    </div>

                    <div className="col-md-5 d-flex gap-2 align-items-center">
                        <Combobox
                            onSelect={(e) => {
                                setSelected(e);
                                updateFilter('filter', e?.[0]?.id);
                            }}
                            selected={selected}
                            label="Status"
                        >
                            <Combobox.Option id="All" label="All" />
                            <Combobox.Option id="LeadAndActive" label="Leads and Active" />
                            <Combobox.Option id="Lead" label="Leads" />
                            <Combobox.Option id="Active" label="Active" />
                            <Combobox.Option id="Archived" label="Archived" />
                        </Combobox>
                        <InputText
                            placeholder="Search"
                            name="firstName"
                            size="small"
                            value={search}
                            onChange={(e) => {
                                debouncedUpdateFilter('searchTerm', e);
                                setSearch(e);
                                setCurrentPage(1);
                            }}
                        />
                        <InstructionToolTip pageurl="pageinstructions" pageName="client-page" />
                    </div>
                </div>
                <div className="mb-3 d-flex gap-3" style={{ paddingBottom: '1rem' }}>
                    <Button label="Manage Columns" onClick={toggleDropdown} />
                    <Button
                        label={"Filter"}
                        onClick={toggleFilter}
                    />
                    <Button label="Clear Filters" type="secondary" onClick={clearFilters} />
                    {isSavePending ? (
                        <Spinner size="base" />
                    ) : (
                        <Button label="Save Settings" type="secondary" onClick={saveUserSettingsHandler} />
                    )}
                </div>
            </div>

            {/* Dropdown for toggling column visibility */}
            {showDropdown && (
                <div className="dropdown__sticky">
                    <Dropdown show={showDropdown}>
                        <Dropdown.Menu>
                            <DropdownItem>Show and Hide Columns</DropdownItem>
                            <div className="ms-3 d-flex flex-column gap-1">
                                {table.getAllLeafColumns().map((column) => {
                                    return (
                                        <div key={column.id} className="px-1">
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    checked={column.getIsVisible()}
                                                    onChange={column.getToggleVisibilityHandler()}
                                                />{' '}
                                                {formatLabel(column.id)}
                                            </label>
                                        </div>
                                    );
                                })}
                            </div>
                        </Dropdown.Menu>
                    </Dropdown>
                </div>
            )}

            {/* Table with DnD for whole‑column dragging */}
            <div>
                <div
                    id="client__table"
                    className="mt-2 rounded-lg clienttable__sticky bg-white overflow-y-auto"
                >
                    <DndContext
                        collisionDetection={closestCenter}
                        modifiers={[restrictToHorizontalAxis]}
                        onDragEnd={handleDragEnd}
                        sensors={sensors}
                    >
                        <table
                            className="table table-hover border border-collapse border-gray-300"
                            style={{
                                ...columnSizeVars,
                                width: table.getTotalSize(),
                            }}
                        >
                            <thead>
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <SortableContext
                                        key={headerGroup.id}
                                        items={columnOrder}
                                        strategy={horizontalListSortingStrategy}
                                    >
                                        <tr key={headerGroup.id}>
                                            {headerGroup.headers.map((header) => (
                                                <DraggableTableHeader key={header.id} header={header} onSort={onSort} getIconStyle={getIconStyle} />
                                            ))}
                                        </tr>
                                    </SortableContext>
                                ))}
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    Array.from({ length: 2 }).map((_, index) => (
                                        <tr key={index}>
                                            {table.getAllLeafColumns().map((col) => (
                                                <td key={col.id}>
                                                    <Glimmer shape="rectangle" size="base" timing="speed" />
                                                </td>
                                            ))}
                                        </tr>
                                    ))
                                ) : table.getRowModel().rows.length === 0 ? (
                                    <tr>
                                        <td colSpan={columns.length} style={{ textAlign: 'center', padding: '2rem' }}>
                                            No data found
                                        </td>
                                    </tr>
                                ) : (
                                    table.getRowModel().rows.map((row) => (
                                        <tr key={row.id} className="border">
                                            {row.getVisibleCells().map((cell) => (
                                                <SortableContext
                                                    key={cell.id}
                                                    items={columnOrder}
                                                    strategy={horizontalListSortingStrategy}
                                                >
                                                    <DragAlongCell key={cell.id} row={row} cell={cell} />
                                                </SortableContext>
                                            ))}
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </DndContext>
                </div>
            </div>

            {/* Pagination */}
            <div className="d-md-flex justify-content-between mt-4">
                <div>
                    <p>{clientResponse?.totalItems ?? 0} entries</p>
                </div>
                {clientResponse && clientResponse.totalPages > 1 && (
                    <Pagination
                        currentPage={clientResponse.currentPage}
                        totalPages={clientResponse.totalPages}
                        onPageChange={(page) => {
                            setCurrentPage(page);
                            updateFilter('page', page);
                        }}
                    />
                )}
            </div>
        </div>
    );
}
